import { useEffect, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useCategoryFeatureNames } from './api';

interface EditCategoryFeaturesModalProps {
  categoryId: string | null;
  categoryTitle: string;
  busy?: boolean;
  onSubmit: (features: string[]) => void;
  onClose: () => void;
}

// Legacy offered a multi-select of all host feature definitions
// (IFeatureDefinitionManager.GetAllAsync). The SPA has no feature-definition
// lookup endpoint, so features are edited as a line-separated list of feature
// names, pre-loaded from the category's current assignment. Submitting posts
// the full set to UpdateCategoryFeaturesAsync, which diffs add/remove.
export function EditCategoryFeaturesModal({
  categoryId,
  categoryTitle,
  busy = false,
  onSubmit,
  onClose,
}: EditCategoryFeaturesModalProps) {
  const query = useCategoryFeatureNames(categoryId);
  const [text, setText] = useState('');

  useEffect(() => {
    if (query.data) setText(query.data.join('\n'));
  }, [query.data]);

  function handleSave() {
    const features = text
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    onSubmit(features);
  }

  return (
    <Modal
      show={categoryId !== null}
      title={`Category Features — ${categoryTitle}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            No
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy || query.isLoading}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {query.isLoading ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorState message="Could not load the category features." onRetry={() => query.refetch()} />
      ) : (
        <div className="mb-1">
          <label htmlFor="category-features" className="form-label">
            Feature names (one per line)
          </label>
          <textarea
            id="category-features"
            className="form-control"
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
